// src/pages/adminDashboard/components/CompanyTable.js
import React, {useMemo, useState} from "react";
import { Table, Tooltip, Button, Tag } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { fetchSingleInstitution } from "../AdminDashboardThunk";
import dayjs from "dayjs";

const CompanyTable = ({ onEdit }) => {
  const { list, loading, filters  } = useSelector((s) => s.adminDashboard);
  const dispatch = useDispatch();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };


  const filteredList = useMemo(() => {
    if (!filters?.fromDate || !filters?.toDate) return list;

    return list.filter((item) => {
      if (!item.created_date) return false;

      const created = dayjs(item.created_date);

      return (
        // created.isAfter(dayjs(filters.fromDate)) &&
        // created.isBefore(dayjs(filters.toDate))
        created.isAfter(dayjs(filters.fromDate).subtract(1, "second")) && //Use inclusive:
        created.isBefore(dayjs(filters.toDate).add(1, "second"))
      );
    });
  }, [list, filters]);

  const columns = [
    { title: "No.", render: (_, __, idx) => idx + 1 },

    {
      title: "Company",
      dataIndex: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (val) => <Tooltip title={val}>{val || "-"}</Tooltip>,
    },

    { title: "Email", dataIndex: "company_email" },
    {
      title: "Admin Email",
      dataIndex: "admin_email",
      
    },
    {
      title: "Registered On",
      dataIndex: "created_at", // 🔁 make sure this matches your API field
      sorter: (a, b) =>
        dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      defaultSortOrder: "ascend", // ✅ oldest first, newest last
      render: (val) =>
        val ? dayjs(val).format("DD-MMM-YYYY") : "-",
    },
    // ✅ Phone No with country code
    {
      title: "Phone No",
      render: (r) => {
        const code = r.country_code ?? "";
        const phone = r.phone_no ?? "";
        return phone ? `+${code} ${phone}` : "-";
      },
    },

    { title: "Country", dataIndex: "country" },
    { title: "Subscription Type", dataIndex: "subscription_type" },

    // ✅ Status (0=Inactive, 1=Active, 2=Expiring Soon)
    {
      title: "Status",
      dataIndex: "subscription_status",

      sorter: (a, b) =>
        (a.subscription_status || "").localeCompare(
          b.subscription_status || ""
        ),

      render: (val, record) => {
        // Preserve explicitly inactive companies if Status represents
        // administrative account activation.
        if (record.Status === 0) {
          return <Tag color="red">Inactive</Tag>;
        }

        if (val === "EXPIRED") {
          return <Tag color="red">Expired</Tag>;
        }

        if (val === "EXPIRING_SOON") {
          return <Tag color="orange">Expiring Soon</Tag>;
        }

        if (val === "ACTIVE") {
          return <Tag color="green">Active</Tag>;
        }

        return "-";
      },
    },

    // ✅ Plan Expiry with color
    {
  title: "Plan Expiry",
  dataIndex: "subscription_expiry",

  sorter: (a, b) =>
    dayjs(a.subscription_expiry).unix() -
    dayjs(b.subscription_expiry).unix(),

  render: (val, record) => {
    if (!val) return "-";

    let color = "blue";

    if (record.subscription_status === "EXPIRED") {
      color = "red";
    } else if (record.subscription_status === "EXPIRING_SOON") {
      color = "orange";
    }

    return (
      <Tag color={color}>
        {dayjs(val).format("DD-MMM-YYYY")}
      </Tag>
    );
  },
},

    {
      title: "Created Date",
      dataIndex: "created_date",
      sorter: (a, b) =>
        dayjs(a.created_date).unix() - dayjs(b.created_date).unix(),
      render: (val) =>
        val ? dayjs(val).format("DD-MMM-YYYY") : "-",
    },

    {
      title: "Branch Count",
      render: (r) =>
        `${r.used_branch_count ?? 0}/${r.total_branch_count ?? 0}`,
    },
    {
      title: "Total Users",
      render: (r) => `${r.used_user_count ?? 0}/${r.total_user_count ?? 0}`,
    },
    {
      title: "Total Basic Users",
      render: (r) =>
        `${r.used_basic_count ?? 0}/${r.basic_user_limit ?? 0}`,
    },
    {
      title: "Total Core Users",
      render: (r) =>
        `${r.used_core_count ?? 0}/${r.core_user_limit ?? 0}`,
    },

    // 🟢 Extra (old UI had them, optional to show)
    // { title: "Admin Name", dataIndex: "admin_name" },
    { title: "Time Zone", dataIndex: "time_zone" },

    {
      title: "Action",
      render: (r) => (
        <Button
          type="link"
          onClick={() => {
            dispatch(fetchSingleInstitution(r.id)).then(() => onEdit());
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Table
      data-testid="company-table"
      rowKey="id"
      loading={loading}
      dataSource={filteredList}
      columns={columns}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}
      scroll={{ x: "max-content" }}
    />
  );
};

export default CompanyTable;
